import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Plus, Pencil, Trash2, Minus, Image as ImageIcon, Eye, EyeOff, X, Star, MessageSquarePlus, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '../context/SettingsContext';

const PAGE_SIZE = 25;

const EMPTY = {
  name: '', description: '', price: 0, stock: 0, category: '', image: '',
  images: [], size: '', weight: '', product_type: '', features: [],
  is_new: false, is_offer: false, offer_price: null, hidden: false,
};

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

export default function AdminProducts() {
  const { settings } = useSettings();
  const threshold = Math.max(1, parseInt(settings?.low_stock_threshold, 10) || 5);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [adminPage, setAdminPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({ name: '', rating: 5, comment: '' });
  const [newFeature, setNewFeature] = useState('');
  const searchTimer = useRef(null);

  const load = useCallback(async (page = 1, q = '') => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get('/products/admin', { params: { page, limit: PAGE_SIZE, search: q || undefined } }),
        categories.length === 0 ? api.get('/categories') : Promise.resolve(null),
      ]);
      const data = p.data;
      if (Array.isArray(data)) {
        setProducts(data);
        setTotal(data.length);
        setTotalPages(1);
      } else {
        setProducts(data.data || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
      }
      if (c) setCategories(c.data);
    } catch {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  }, [categories.length]);

  useEffect(() => { load(1, ''); }, [load]);

  // Búsqueda con debounce 350ms
  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setAdminPage(1);
      load(1, val);
    }, 350);
  };

  const goPage = (p) => {
    setAdminPage(p);
    load(p, search);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openNew = () => {
    setForm(EMPTY);
    setEditing(null);
    setReviews([]);
    setDialog(true);
  };

  const openEdit = async (p) => {
    setForm({
      ...EMPTY, ...p,
      images: p.images || [],
      features: p.features || [],
      offer_price: p.offer_price ?? null,
    });
    setEditing(p.id);
    setDialog(true);
    try {
      const { data } = await api.get(`/products/${p.id}/reviews`);
      setReviews(data);
    } catch { setReviews([]); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        stock: Number(form.stock),
        offer_price: form.offer_price ? Number(form.offer_price) : null,
      };
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post('/products', payload);
      toast.success(editing ? 'Producto actualizado' : 'Producto creado');
      setDialog(false);
      load(adminPage, search);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    await api.delete(`/products/${id}`);
    toast.success('Eliminado');
    // Quitar de la lista local sin recargar
    setProducts((prev) => prev.filter((p) => p.id !== id));
    setTotal((t) => t - 1);
  };

  // Actualiza el stock en estado local (sin recargar todo el listado)
  const adjustStock = async (id, delta) => {
    try {
      await api.post(`/products/${id}/stock`, { delta });
      setProducts((prev) =>
        prev.map((p) => p.id === id ? { ...p, stock: Math.max(0, p.stock + delta) } : p)
      );
    } catch {
      toast.error('Error al ajustar stock');
    }
  };

  const onMainImage = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error('Imagen demasiado grande (máx 3MB)'); return; }
    const url = await fileToDataUrl(file);
    setForm((f) => ({ ...f, image: url }));
  };

  const onAddGalleryImage = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error('Imagen demasiado grande (máx 3MB)'); return; }
    const url = await fileToDataUrl(file);
    setForm((f) => ({ ...f, images: [...(f.images || []), url] }));
  };

  const removeGalleryImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const addFeature = () => {
    if (!newFeature.trim()) return;
    setForm((f) => ({ ...f, features: [...(f.features || []), newFeature.trim()] }));
    setNewFeature('');
  };

  const removeFeature = (idx) => {
    setForm((f) => ({ ...f, features: f.features.filter((_, i) => i !== idx) }));
  };

  const addReview = async () => {
    if (!editing || !newReview.name.trim() || !newReview.comment.trim()) return;
    try {
      const { data } = await api.post(`/products/${editing}/reviews`, {
        name: newReview.name,
        rating: Number(newReview.rating),
        comment: newReview.comment,
      });
      setReviews((r) => [data, ...r]);
      setNewReview({ name: '', rating: 5, comment: '' });
      toast.success('Reseña agregada');
    } catch { toast.error('Error al agregar reseña'); }
  };

  const deleteReview = async (reviewId) => {
    if (!editing) return;
    await api.delete(`/products/${editing}/reviews/${reviewId}`);
    setReviews((r) => r.filter((x) => x.id !== reviewId));
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-slate-900">Productos</h1>
          <p className="text-slate-500 text-sm">{total} productos en el catálogo</p>
        </div>
        <Button onClick={openNew} data-testid="add-product-btn" className="btn-pink rounded-full">
          <Plus className="h-4 w-4 mr-1" /> Nuevo producto
        </Button>
      </div>

      {/* Barra de búsqueda */}
      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          data-testid="admin-search-input"
          placeholder="Buscar por nombre o descripción…"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="pl-9 rounded-xl border-slate-200"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left p-3">Producto</th>
              <th className="text-left p-3">Categoría</th>
              <th className="text-left p-3">Precio</th>
              <th className="text-left p-3">Stock</th>
              <th className="text-left p-3">Estado</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && products.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-slate-400">
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-4 w-4 rounded-full bg-pinky-300 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="h-4 w-4 rounded-full bg-pinky-300 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="h-4 w-4 rounded-full bg-pinky-300 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </td>
              </tr>
            ) : products.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-slate-400">No se encontraron productos</td>
              </tr>
            ) : (
              products.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-product-row-${p.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {p.image ? (
                        <img
                          src={p.image}
                          alt=""
                          className="h-12 w-12 rounded-lg object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400"><ImageIcon className="h-5 w-5" /></div>
                      )}
                      <div>
                        <p className="font-semibold text-slate-900">{p.name}</p>
                        <div className="flex gap-1 mt-1">
                          {p.is_new && <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 font-bold">Nuevo</span>}
                          {p.is_offer && <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-bold">Oferta</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-slate-600">{p.category}</td>
                  <td className="p-3 font-semibold">
                    Q{p.price}
                    {p.is_offer && p.offer_price && <span className="text-xs text-pinky-600 ml-1">(Q{p.offer_price})</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <span className={`font-bold ${p.stock === 0 ? 'text-rose-600' : p.stock <= threshold ? 'text-amber-600' : 'text-emerald-600'}`}>{p.stock}</span>
                      <button onClick={() => adjustStock(p.id, -5)} className="ml-2 text-xs px-2 py-0.5 rounded bg-rose-50 text-rose-600 hover:bg-rose-100" data-testid={`stock-m5-${p.id}`}>-5</button>
                      <button onClick={() => adjustStock(p.id, -2)} className="text-xs px-2 py-0.5 rounded bg-rose-50 text-rose-600 hover:bg-rose-100" data-testid={`stock-m2-${p.id}`}>-2</button>
                      <button onClick={() => adjustStock(p.id, -1)} className="text-xs px-2 py-0.5 rounded bg-rose-50 text-rose-600 hover:bg-rose-100" data-testid={`stock-m1-${p.id}`}><Minus className="h-3 w-3" /></button>
                      <button onClick={() => adjustStock(p.id, 1)} className="text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-600 hover:bg-emerald-100" data-testid={`stock-p1-${p.id}`}><Plus className="h-3 w-3" /></button>
                    </div>
                  </td>
                  <td className="p-3">
                    {p.hidden ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold"><EyeOff className="h-3 w-3" /> Oculto</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold"><Eye className="h-3 w-3" /> Visible</span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => openEdit(p)} className="h-8 w-8 rounded-lg hover:bg-pinky-50 text-pinky-600 inline-flex items-center justify-center" data-testid={`edit-${p.id}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(p.id)} className="h-8 w-8 rounded-lg hover:bg-rose-50 text-rose-600 inline-flex items-center justify-center ml-1" data-testid={`delete-${p.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Controles de paginación */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50">
            <span className="text-xs text-slate-500">
              Página {adminPage} de {totalPages} · {total} productos
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => goPage(adminPage - 1)}
                disabled={adminPage <= 1 || loading}
                className="rounded-lg h-8 px-3"
                data-testid="admin-prev-page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = adminPage <= 3
                  ? i + 1
                  : adminPage >= totalPages - 2
                    ? totalPages - 4 + i
                    : adminPage - 2 + i;
                if (p < 1 || p > totalPages) return null;
                return (
                  <Button
                    key={p}
                    variant={p === adminPage ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => goPage(p)}
                    disabled={loading}
                    className={`rounded-lg h-8 w-8 p-0 text-xs ${p === adminPage ? 'btn-pink' : ''}`}
                    data-testid={`admin-page-${p}`}
                  >
                    {p}
                  </Button>
                );
              })}
              <Button
                variant="outline"
                size="sm"
                onClick={() => goPage(adminPage + 1)}
                disabled={adminPage >= totalPages || loading}
                className="rounded-lg h-8 px-3"
                data-testid="admin-next-page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? 'Editar producto' : 'Nuevo producto'}</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="info" className="w-full">
            <TabsList className="grid grid-cols-4 mb-4">
              <TabsTrigger value="info" data-testid="tab-info">Información</TabsTrigger>
              <TabsTrigger value="details" data-testid="tab-details">Detalles</TabsTrigger>
              <TabsTrigger value="gallery" data-testid="tab-gallery">Galería</TabsTrigger>
              <TabsTrigger value="reviews" disabled={!editing} data-testid="tab-reviews">Reseñas {editing ? `(${reviews.length})` : ''}</TabsTrigger>
            </TabsList>

            {/* INFO */}
            <TabsContent value="info" className="space-y-4 mt-0">
              <div>
                <Label>Imagen principal *</Label>
                <div className="mt-1 flex items-center gap-3">
                  {form.image ? (
                    <img src={form.image} alt="" className="h-24 w-24 rounded-xl object-cover border border-slate-200" />
                  ) : (
                    <div className="h-24 w-24 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                      <ImageIcon className="h-8 w-8" />
                    </div>
                  )}
                  <input type="file" accept="image/*" onChange={(e) => onMainImage(e.target.files?.[0])} data-testid="product-image-input" className="text-sm" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Nombre *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" className="rounded-xl" />
                </div>
                <div className="col-span-2">
                  <Label>Descripción</Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-desc-input" className="rounded-xl" />
                </div>
                <div>
                  <Label>Precio (Q) *</Label>
                  <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price-input" className="rounded-xl" />
                </div>
                <div>
                  <Label>Stock *</Label>
                  <Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="product-stock-input" className="rounded-xl" />
                </div>
                <div className="col-span-2">
                  <Label>Categoría *</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="product-category-select" className="rounded-xl"><SelectValue placeholder="Selecciona categoría" /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 p-4 bg-pinky-50 rounded-2xl">
                <div className="flex items-center justify-between col-span-2">
                  <Label>✨ Etiqueta Nuevo</Label>
                  <Switch checked={form.is_new} onCheckedChange={(v) => setForm({ ...form, is_new: v })} data-testid="product-new-switch" />
                </div>
                <div className="flex items-center justify-between col-span-2">
                  <Label>🔥 En oferta</Label>
                  <Switch checked={form.is_offer} onCheckedChange={(v) => setForm({ ...form, is_offer: v })} data-testid="product-offer-switch" />
                </div>
                {form.is_offer && (
                  <div className="col-span-2">
                    <Label>Precio de oferta (Q)</Label>
                    <Input type="number" value={form.offer_price || ''} onChange={(e) => setForm({ ...form, offer_price: e.target.value })} data-testid="product-offer-price" className="rounded-xl" />
                  </div>
                )}
                <div className="flex items-center justify-between col-span-2">
                  <Label>Ocultar del catálogo</Label>
                  <Switch checked={form.hidden} onCheckedChange={(v) => setForm({ ...form, hidden: v })} data-testid="product-hidden-switch" />
                </div>
              </div>
            </TabsContent>

            {/* DETAILS */}
            <TabsContent value="details" className="space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo de producto</Label>
                  <Input value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })} placeholder="Ej. Peluche oficial, Taza cerámica" className="rounded-xl" data-testid="product-type-input" />
                </div>
                <div>
                  <Label>Tamaño</Label>
                  <Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="Ej. 30cm de alto, Talla M" className="rounded-xl" data-testid="product-size-input" />
                </div>
                <div className="col-span-2">
                  <Label>Peso</Label>
                  <Input value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} placeholder="Ej. 250g, 1.2kg" className="rounded-xl" data-testid="product-weight-input" />
                </div>
              </div>

              <div>
                <Label>Características (lista)</Label>
                <div className="mt-2 flex gap-2">
                  <Input
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFeature(); } }}
                    placeholder="Ej. Licencia oficial Disney"
                    className="rounded-xl"
                    data-testid="product-feature-input"
                  />
                  <Button type="button" onClick={addFeature} className="btn-pink rounded-full" data-testid="product-feature-add">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {form.features?.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {form.features.map((f, i) => (
                      <li key={`${f}-${i}`} className="flex items-center gap-2 p-2 rounded-xl bg-pinky-50 border border-pinky-100">
                        <span className="flex-1 text-sm text-slate-700">• {f}</span>
                        <button onClick={() => removeFeature(i)} className="text-rose-500 hover:bg-rose-50 h-7 w-7 rounded-lg inline-flex items-center justify-center" data-testid={`feature-remove-${i}`}>
                          <X className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </TabsContent>

            {/* GALLERY */}
            <TabsContent value="gallery" className="space-y-4 mt-0">
              <div>
                <Label>Imágenes adicionales (galería)</Label>
                <p className="text-xs text-slate-500 mt-1 mb-3">Las mostradas en la página de detalle. La primera es la principal (pestaña Información).</p>
                <input type="file" accept="image/*" onChange={(e) => { onAddGalleryImage(e.target.files?.[0]); e.target.value = ''; }} className="text-sm" data-testid="product-gallery-input" />
                {form.images?.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {form.images.map((img, i) => (
                      <div key={`${img.slice(-32)}-${i}`} className="relative group" data-testid={`gallery-item-${i}`}>
                        <img src={img} alt="" className="w-full aspect-square rounded-xl object-cover border border-slate-200" />
                        <button onClick={() => removeGalleryImage(i)} className="absolute top-1 right-1 h-7 w-7 rounded-full bg-rose-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-testid={`gallery-remove-${i}`}>
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* REVIEWS */}
            <TabsContent value="reviews" className="space-y-4 mt-0">
              {editing ? (
                <>
                  <div className="p-4 rounded-2xl bg-pinky-50 border border-pinky-100 space-y-3">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-1 text-sm"><MessageSquarePlus className="h-4 w-4 text-pinky-500" /> Agregar reseña</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={newReview.name} onChange={(e) => setNewReview({ ...newReview, name: e.target.value })} placeholder="Nombre del cliente" className="rounded-xl" data-testid="review-name-input" />
                      <Select value={String(newReview.rating)} onValueChange={(v) => setNewReview({ ...newReview, rating: Number(v) })}>
                        <SelectTrigger className="rounded-xl" data-testid="review-rating-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[5, 4, 3, 2, 1].map((r) => <SelectItem key={r} value={String(r)}>{'⭐'.repeat(r)} ({r})</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Textarea rows={2} value={newReview.comment} onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })} placeholder="Comentario del cliente" className="rounded-xl" data-testid="review-comment-input" />
                    <Button type="button" onClick={addReview} disabled={!newReview.name || !newReview.comment} className="btn-pink rounded-full w-full" data-testid="review-add-btn">
                      Agregar reseña
                    </Button>
                  </div>

                  {reviews.length === 0 ? (
                    <p className="text-center text-slate-500 py-6 text-sm">Todavía no hay reseñas para este producto.</p>
                  ) : (
                    <ul className="space-y-2">
                      {reviews.map((r) => (
                        <li key={r.id} className="p-3 rounded-xl border border-slate-200 flex gap-3" data-testid={`review-row-${r.id}`}>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-sm">{r.name}</span>
                              <span className="text-amber-500 text-xs">
                                {[...Array(r.rating)].map((_, i) => <Star key={`rev-${r.id}-${i}`} className="inline h-3 w-3 fill-amber-400" />)}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 mt-1">{r.comment}</p>
                          </div>
                          <button onClick={() => deleteReview(r.id)} className="text-rose-500 hover:bg-rose-50 h-8 w-8 rounded-lg inline-flex items-center justify-center flex-shrink-0" data-testid={`review-delete-${r.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <p className="text-center text-slate-500 py-6 text-sm">Guarda el producto primero para agregar reseñas.</p>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.name || !form.category} className="btn-pink rounded-full" data-testid="product-save-btn">
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
